import { PointerEvent, useCallback, useRef, useState } from "react";

const MIN_SIDEBAR_WIDTH = 300;
const DEFAULT_SIDEBAR_WIDTH = 450;

// The drawer chrome: whether the panel is open and how wide it is
// (drag-resizable).
//
// Resizing tracks the pointer via pointer capture on the splitter. While the
// drag is in flight the width is written straight to the drawer element's
// --sidebar-width custom property, bypassing React state so the whole page
// subtree isn't re-rendered per pointermove; the final width is committed to
// state once on release.
export function useSidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);

  const drawerRef = useRef<HTMLDivElement | null>(null);
  const liveWidthRef = useRef(width);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const onSplitterPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
  }, []);

  const onSplitterPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    const next = Math.max(MIN_SIDEBAR_WIDTH, window.innerWidth - event.clientX);
    liveWidthRef.current = next;
    drawerRef.current?.style.setProperty("--sidebar-width", `${next}px`);
  }, []);

  const onSplitterPointerEnd = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    setIsResizing(false);
    setWidth(liveWidthRef.current);
  }, []);

  const splitterProps = {
    onPointerDown: onSplitterPointerDown,
    onPointerMove: onSplitterPointerMove,
    onPointerUp: onSplitterPointerEnd,
    onPointerCancel: onSplitterPointerEnd,
  };

  // Mid-drag re-renders (e.g. a query settling) re-apply the drawer's style
  // prop; using the live value while resizing keeps them from snapping the
  // width back to the last committed state.
  const displayWidth = isResizing ? liveWidthRef.current : width;

  return {
    isOpen,
    width: displayWidth,
    isResizing,
    drawerRef,
    splitterProps,
    open,
    close,
    toggle,
  };
}
